import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { User, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";

const AuthorsPage = () => {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAuthors();
  }, []);

  const fetchAuthors = async () => {
    try {
      const response = await axios.get(`${API}/authors`);
      setAuthors(response.data);
    } catch (error) {
      console.error("Failed to fetch authors:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen" data-testid="authors-page">
      <SEO 
        title="Writers & Creatives"
        description="Meet the creative voices behind Mancunia Rebél. Writers, photographers, and artists empowering the independent creative scene."
        url="/authors"
        keywords={["writers", "creatives", "contributors"]}
      />
      <section className="py-12 md:py-16 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <span className="font-mono text-xs uppercase tracking-widest text-primary">Team</span>
          <h1 className="font-heading font-black text-5xl md:text-7xl uppercase tracking-tighter mt-2">
            Our Creatives
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl">
            The voices behind Mancunia Rebél. Empowering independent creatives across music, fashion, film, and the arts.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          {authors.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-muted-foreground text-lg">No authors found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {authors.map((author) => (
                <Link
                  key={author.author_id}
                  to={`/authors/${author.author_id}`}
                  className="group border border-border p-8 hover:border-primary/50 transition-all duration-300"
                  data-testid={`author-card-${author.author_id}`}
                >
                  <div className="flex flex-col items-start">
                    {author.avatar ? (
                      <img
                        src={author.avatar}
                        alt={author.name}
                        className="w-24 h-24 rounded-full object-cover img-grayscale mb-6"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-secondary flex items-center justify-center mb-6">
                        <User className="h-12 w-12 text-muted-foreground" />
                      </div>
                    )}
                    <span className="font-mono text-xs uppercase tracking-widest text-primary mb-2">
                      {author.role}
                    </span>
                    <h3 className="font-heading font-bold text-2xl uppercase tracking-tight group-hover:text-primary transition-colors">
                      {author.name}
                    </h3>
                    <p className="text-muted-foreground mt-3 line-clamp-3">
                      {author.bio}
                    </p>
                    <div className="flex items-center gap-2 mt-6 text-muted-foreground group-hover:text-primary transition-colors">
                      <span className="font-mono text-xs uppercase tracking-widest">View Profile</span>
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AuthorsPage;
